import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { LineupsIcon } from '@sorare/core/src/atoms/icons/LineupsIcon';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BodyL } from '@sorare/core/src/atoms/typography';
import { LineupStyle } from '@sorare/core/src/components/lobby/ContenderLayout';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { AddTeamLayout } from '../AddTeamLayout';
import type { AddTeamWrapper_so5LeaderboardGroupInterface } from './__generated__/index.graphql';

const AddTeamRoot = styled(Vertical).attrs({ center: true, gap: 4 })`
  width: 100%;
  padding: calc(6 * var(--unit)) var(--double-unit);
  justify-content: space-between;
  flex: 1;

  &.inline {
    padding: calc(3 * var(--unit)) var(--double-unit);
  }
`;

const MainContent = styled(Vertical)`
  flex: 1;
`;

export const AddTeamWrapper = ({
  to,
  onClick,
  children,
  so5LeaderboardGroup,
  lineupStyle,
  className,
  customCta,
}: {
  to?: string;
  onClick?: () => void;
  children: ReactNode;
  so5LeaderboardGroup: AddTeamWrapper_so5LeaderboardGroupInterface;
  lineupStyle: LineupStyle;
  className?: string;
  customCta?: ReactNode;
}) => {
  return (
    <AddTeamLayout
      className={className}
      so5LeaderboardGroup={so5LeaderboardGroup}
      lineupStyle={lineupStyle}
    >
      <AddTeamRoot className={classNames(lineupStyle)}>
        <MainContent gap={2} center>
          <LineupsIcon size={4} />
          <BodyL bold>
            <FormattedMessage {...fantasy.registerNewTeam} />
          </BodyL>
          <LinkOverlay as={ButtonBase} to={to} onClick={onClick}>
            {customCta || (
              <Button asSpan color="secondary" size="medium">
                <FormattedMessage {...fantasy.addTeam} />
              </Button>
            )}
          </LinkOverlay>
        </MainContent>
        {children}
      </AddTeamRoot>
    </AddTeamLayout>
  );
};

AddTeamWrapper.fragments = {
  so5LeaderboardGroupInterface: gql`
    fragment AddTeamWrapper_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      ...AddTeamLayout_so5LeaderboardGroupInterface
    }
    ${AddTeamLayout.fragments.so5LeaderboardGroupInterface}
  ` as TypedDocumentNode<AddTeamWrapper_so5LeaderboardGroupInterface>,
};
